import React, { useState, useRef, useEffect, useMemo } from "react";
import { Text, Flex, Button, DropdownMenu, Progress, Badge, HoverCard, Tooltip, Box, TextField } from "@radix-ui/themes";
import { LocationMultiSelect, BrandMultiSelect } from "components";
import { useGetCustomers, useGetActiveIntegrations, useGetAllBrands } from "hooks";
import { useLocationContext, useProfileContext } from "context";
import { ChevronDownIcon, CaretSortIcon, CaretUpIcon, CaretDownIcon, Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { ReactComponent as FacebookIcon } from 'assets/icons/facebook.svg';
import { ReactComponent as GoogleIcon } from 'assets/icons/google.svg';
import { ReactComponent as AppleIcon } from 'assets/icons/apple.svg';
import { MultiSelect } from "components/multiSelect/MultiSelect";
import { Table, Card } from "@radix-ui/themes";
import NoResults from "./NoResults";
import { attemptGetCustomers } from "api/UsersApi";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FilterByMultiSelect } from "components/multiSelect/FilterByMultiSelect";
import { ZonesMultiSelect } from "components/zonesMultiSelect/ZonesMultiSelect";
import { useGetZones, useGetAirshipStatus, useGetMetaStatus } from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { DatePickerWithRange } from "components/datepicker/DatePickerWithRange";
import { formatInternationalPhoneNumber } from "helpers/phoneFormatting";

// Custom component to handle text truncation and tooltip for column header text
const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };
    
    // Initial check
    checkTruncation();
    
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);
  
  const textElement = (
    <Box as="div" 
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      <Text 
        size={size} 
        weight={weight}
        color={color}
      >
        {text}
      </Text>
    </Box>
  );
  
  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

export default function Customers() {
  const navigate = useNavigate();
  const { embeddedInGuestWifi = false } = useOutletContext() || {};
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();

  const { initialItems, locationSelection, setLocationSelection, allLocations } = useLocationContext();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [emailSearchInput, setEmailSearchInput] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const { data: activeIntegrationsData, isLoading: isIntegrationsLoading } = useGetActiveIntegrations(userProfile?.organisation_id);
  const { data: airshipStatusData, isLoading: isAirshipStatusLoading } = useGetAirshipStatus(userProfile.organisation_id);
  const { data: metaStatusData, isLoading: isMetaStatusLoading } = useGetMetaStatus(userProfile.organisation_id);


  // Function to check if Mailchimp integration is active
  const isMailchimpActive = () => {
    if (!activeIntegrationsData?.data?.integrations) return false;
    return activeIntegrationsData.data.integrations.some((integration) => integration.name === "mailchimp");
  };

  // Function to check if Airship integration is active
  const isAirshipActive = () => {
    if (isAirshipStatusLoading) return false;
    return airshipStatusData?.data?.is_active === true;
  };

  const isMetaActive = () => {
    if (isMetaStatusLoading) return false;
    return metaStatusData?.data?.is_active === true;
  };

  const [zoneSelection, setZoneSelection] = useState([]);

  // To fetch zones data based on location selection
  const { data: zonesData, isLoading: isZonesLoading } = useGetZones(locationSelection?.map((location) => parseInt(location)) || []);

  useEffect(() => {
    if (zonesData && zonesData.length > 0) {
      const allZoneIds = zonesData.map((zone) => zone.id.toString());
      setZoneSelection(["null", ...allZoneIds]);
    }
  }, [zonesData]);

  // Define zone options
  const zoneOptions = zonesData && zonesData.length > 0 ? [...zonesData, { id: "null", name: "No zone" }] : [];

  const marketingOptions = [
    { label: "Subscribed", value: "subscribed" },
    { label: "Unsubscribed", value: "unsubscribed" },
  ];

  const smsMarketingOptions = [
    { label: "Subscribed", value: "subscribed" },
    { label: "Unsubscribed", value: "unsubscribed" },
  ];

  // Defining the gender options
  const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Other", value: "others" },
    { label: "No Response", value: "field_disabled" },
  ];

  // Defining the age options
  const ageOptions = [
    { label: "Under 18", value: "Under 18" },
    { label: "18-24", value: "18-24" },
    { label: "25-34", value: "25-34" },
    { label: "35-44", value: "35-44" },
    { label: "45-54", value: "45-54" },
    { label: "55-64", value: "55-64" },
    { label: "65+", value: "65+" },
    { label: "No Response", value: "field_disabled" },
  ];

  const authTypeOptions = [
    { label: "Email", value: "email" },
    { label: "Facebook", value: "facebook" },
    { label: "Google", value: "google" },
    { label: "Apple", value: "apple" }
  ]

  const filterOptions = [
    { label: "Email marketing", value: "email_marketing_status" },
    { label: "SMS marketing", value: "sms_marketing_status" },
    { label: "Age", value: "age" },
    { label: "Gender", value: "gender" },
    { label: "Access method", value: "auth_type"},
    { label: "Created on", value: "created_on" },
    { label: "Last seen", value: "last_seen" }
  ];

  // Initialize selections with all options
  const [ageSelection, setAgeSelection] = useState(ageOptions.map((option) => option.value));
  const [genderSelection, setGenderSelection] = useState(genderOptions.map((option) => option.value));
  const [marketingSelection, setMarketingSelection] = useState(marketingOptions.map((option) => option.value));
  const [smsMarketingSelection, setSmsMarketingSelection] = useState(smsMarketingOptions.map((option) => option.value)); 
  const [authTypeSelection, setAuthTypeSelection] = useState(authTypeOptions.map((option) => option.value));
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [createdOnDateRange, setCreatedOnDateRange] = useState({ from: null, to: null });
  const [lastSeenDateRange, setLastSeenDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const timer = setTimeout(() => {
      setEmailSearch(emailSearchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [emailSearchInput]);

  const handleCategoryChange = (newCategories) => {
    if (!newCategories.includes("created_on") && selectedCategories.includes("created_on")) {
      setCreatedOnDateRange({ from: null, to: null });
    }
    if (!newCategories.includes("last_seen") && selectedCategories.includes("last_seen")) {
      setLastSeenDateRange({ from: null, to: null });
    }
    setSelectedCategories(newCategories);
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setMarketingSelection(marketingOptions.map((option) => option.value));
    setSmsMarketingSelection(smsMarketingOptions.map((option) => option.value));
    setAgeSelection(ageOptions.map((option) => option.value));
    setGenderSelection(genderOptions.map((option) => option.value));
    setAuthTypeSelection(authTypeOptions.map((option) => option.value));
    setCreatedOnDateRange({ from: null, to: null });
    setLastSeenDateRange({ from: null, to: null });
    setEmailSearchInput("");
    setEmailSearch("");
  };

  const [brandSelection, setBrandSelection] = useState([]);
  const [showBrandFilter, setShowBrandFilter] = useState(false);
  const [filteredLocationOptions, setFilteredLocationOptions] = useState(initialItems || []);

  // Get brands data using your existing hook - add near other API calls
  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);

  // Format brands for dropdown - add after other useMemo hooks
  const formattedBrandOptions = useMemo(() => {
    if (!brandsData || brandsData.length === 0) return [];

    return brandsData.map((brand) => ({
      label: brand.is_default ? "No brand" : brand.name,
      value: brand.id.toString(),
      locations: brand.locations || [],
      isDefault: brand.is_default,
    }));
  }, [brandsData]);

  // Determine if we should show brand filter - add after other useEffect hooks
  useEffect(() => {
    // Only show brand filter if there's more than one brand
    if (formattedBrandOptions.length > 1) {
      setShowBrandFilter(true);
      // Initially select all brands
      const allBrandIds = formattedBrandOptions.map((brand) => brand.value);
      setBrandSelection(allBrandIds);
    } else {
      setShowBrandFilter(false);
    }
  }, [formattedBrandOptions]);

  // Filter locations based on selected brands - add after the above useEffect
  useEffect(() => {
    // Skip if we don't have brand options or brand filter isn't shown
    if (!showBrandFilter || formattedBrandOptions.length <= 1) {
      setFilteredLocationOptions(initialItems);
      return;
    }

    // If no brands selected, show no locations
    if (brandSelection.length === 0) {
      setFilteredLocationOptions([]);
      setLocationSelection([]);
      return;
    }

    // Collect all locations from selected brands
    const selectedLocations = new Set();

    formattedBrandOptions
      .filter((brand) => brandSelection.includes(brand.value))
      .forEach((brand) => {
        if (brand.locations && Array.isArray(brand.locations)) {
          brand.locations.forEach((location) => {
            selectedLocations.add(location.id.toString());
          });
        }
      });

    // Convert to array
    const selectedLocationIds = Array.from(selectedLocations);

    // Filter initialItems to only include locations from selected brands
    const filtered = initialItems.filter((item) => selectedLocationIds.includes(String(item.value)));

    // Update filtered locations
    setFilteredLocationOptions(filtered);

    // IMPROVED SELECTION LOGIC:
    // 1. Check if we have all brands selected
    const allBrandsSelected = brandSelection.length === formattedBrandOptions.length;

    // 2. Check if all available locations are selected
    const allLocationsSelected = filtered.length === locationSelection.length && filtered.every((item) => locationSelection.includes(String(item.value)));

    // If all brands are selected, select all available locations
    if (allBrandsSelected || (brandSelection.length > 0 && !allLocationsSelected)) {
      // Always select all available locations when all brands are selected
      // or when the available locations don't match the current selection
      setLocationSelection(selectedLocationIds);
    } else if (filtered.length === 0) {
      // No locations available
      setLocationSelection([]);
    }
    // Otherwise keep the current selection
  }, [brandSelection, formattedBrandOptions, initialItems, showBrandFilter]);

  // Create filters object when any selection changes
  const filters = useMemo(() => {
    const includeNullZones = zoneSelection.includes("null");
    const nonNullZones = zoneSelection.filter((zone) => zone !== "null");

    return {
      gender: genderSelection,
      age: ageSelection,
      location_ids: locationSelection,
      email_marketing_status: marketingSelection,
      sms_marketing_status: smsMarketingSelection,
      auth_type: authTypeSelection,
      zone_ids: nonNullZones,
      include_null_zones: includeNullZones,
      sort_column: sortColumn,
      sort_direction: sortDirection,
      created_on_start: createdOnDateRange.from ? createdOnDateRange.from.toISOString() : null,
      created_on_end: createdOnDateRange.to ? createdOnDateRange.to.toISOString() : null,
      last_seen_start: lastSeenDateRange.from ? lastSeenDateRange.from.toISOString() : null,
      last_seen_end: lastSeenDateRange.to ? lastSeenDateRange.to.toISOString() : null,
      email_search: emailSearch,
    };
  }, [genderSelection, ageSelection, locationSelection, marketingSelection, smsMarketingSelection, authTypeSelection, zoneSelection, sortColumn, sortDirection, createdOnDateRange, lastSeenDateRange, emailSearch]);

  const { data: customerData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: isLoadingCustomerData } = useGetCustomers(filters, true, ["filteredCustomers", JSON.stringify(filters)]);

  const [trueTotalContacts, setTrueTotalContacts] = useState(0);
  const [activeSubscriptions, setActiveSubscriptions] = useState(0);
  const [optInRate, setOptInRate] = useState(0);

  useEffect(() => {
    const firstPage = customerData?.pages[0];
    if (!firstPage) return;
    
    if (firstPage?.total_contacts !== undefined) {
      setTrueTotalContacts(firstPage.total_contacts);
    }
    if (firstPage.active_subscriptions !== undefined) {
    setActiveSubscriptions(firstPage.active_subscriptions);
    }
    if (firstPage.opt_in_rate !== undefined) {
      setOptInRate(firstPage.opt_in_rate);
    }
  }, [customerData]);

  // Create an all-inclusive filter for the export all functionality
  const allFilters = useMemo(
    () => ({
      gender: genderOptions.map((option) => option.value),
      age: ageOptions.map((option) => option.value),
      location_ids: allLocations,
      email_marketing_status: marketingOptions.map((option) => option.value),
      sms_marketing_status: smsMarketingOptions.map((option) => option.value),
      auth_type: authTypeOptions.map((option) => option.value),
      zone_ids: zonesData ? zonesData.map((zone) => zone.id.toString()) : [],
    }),
    [genderOptions, ageOptions, allLocations, marketingOptions, smsMarketingOptions, zonesData]
  );

  useEffect(() => {
    // Only invalidate filtered customers when filters change
    queryClient.invalidateQueries(["filteredCustomers"]);

    // Don't invalidate totalCustomers
  }, [genderSelection, ageSelection, locationSelection, marketingSelection, authTypeSelection, zoneSelection, sortColumn, sortDirection, createdOnDateRange, lastSeenDateRange, emailSearch, queryClient]);

  const downloadCSV = (data, filename) => {
  if (!Array.isArray(data) || data.length === 0) return;

  // Track which columns actually have data
  const has = {
    fullName:     false,
    email:        false,
    phone:        false,
    emailMarketing: false,
    smsMarketing:   false,
    age:            false,
    dob:            false,
    gender:         false,
    brands:         false,
    locations:      false,
    created:        false,
    lastSeen:       false,
    connections:    false,
  };

  // Build each row as an object and mark used columns
  const rows = data.map((customer) => {
    // Locally compute locations array & connection count
    const locationsArr = Array.isArray(customer.locations_data)
      ? customer.locations_data.map((loc) => loc.location)
      : [];
    const connectionCount = locationsArr.length;

    const row = {};

    // Full Name
    const fullName = [customer.first_name, customer.last_name]
      .filter(Boolean)
      .join(" ");
    if (fullName) {
      has.fullName = true;
      row.fullName = fullName;
    }

    // Email Address (exclude @noemailbwifi.com)
    const email = customer.email_address?.endsWith("@noemailbwifi.com")
      ? ""
      : customer.email_address || "";
    if (email) {
      has.email = true;
      row.email = email;
    }

    // Phone Number
    if (customer.phone_number) {
      has.phone = true;
      row.phone = formatInternationalPhoneNumber(customer.phone_number);
    }

    // Email marketing
    let em = customer.email_marketing_status || "";
    if (locationsArr.length > 1) {
      em = customer.locations_data
        .map((loc) => `${loc.location}:${loc.email_marketing_status || "Unknown"}`)
        .join(", ");
    }
    if (em) {
      has.emailMarketing = true;
      row.emailMarketing = em;
    }

    // SMS marketing
    if (showSmsMarketingColumn) {
      let sm = customer.sms_marketing_status || "";
      if (locationsArr.length > 1) {
        sm = customer.locations_data
          .map((loc) => `${loc.location}:${loc.sms_marketing_status || "Unknown"}`)
          .join(", ");
      }
      if (sm) {
        has.smsMarketing = true;
        row.smsMarketing = sm;
      }
    }

    // Age group
    if (showAgeColumn && customer.age && customer.age !== "field_disabled") {
      has.age = true;
      row.age = customer.age;
    }

    // Date of birth
    if (showDobColumn && customer.dob) {
      has.dob = true;
      row.dob = customer.dob;
    }

    // Gender
    if (showGenderColumn && customer.gender && customer.gender !== "Field_disabled") {
      has.gender = true;
      row.gender = customer.gender === "Others" ? "Other" : customer.gender;
    }

    // Brand(s)
    if (showBrandFilter && Array.isArray(customer.brands) && customer.brands.length) {
      has.brands = true;
      row.brands = customer.brands.join(", ");
    }

    // Location(s)
    if (locationsArr.length) {
      has.locations = true;
      row.locations = locationsArr.join(", ");
    }

    // Created
    if (customer.created) {
      has.created = true;
      row.created = new Date(customer.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' + new Date(customer.created).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
    }

    // Last seen
    if (customer.last_seen) {
      has.lastSeen = true;
      row.lastSeen = new Date(customer.last_seen).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' + new Date(customer.last_seen).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
    }

    // Connection(s)
    if (connectionCount != null) {
      has.connections = true;
      row.connections = connectionCount;
    }

    return row;
  });

  // Define column order and labels
  const keyOrder = [
    "fullName",
    "email",
    "phone",
    "emailMarketing",
    "smsMarketing",
    "age",
    "dob",
    "gender",
    "brands",
    "locations",
    "created",
    "lastSeen",
    "connections",
  ];
  const labels = {
    fullName:       "Name",
    email:          "Email address",
    phone:          "Phone",
    emailMarketing: "Email marketing",
    smsMarketing:   "SMS marketing",
    age:            "Age group",
    dob:            "Date of birth",
    gender:         "Gender",
    brands:         "Brand(s)",
    locations:      "Location(s)",
    created:        "Created on",
    lastSeen:       "Last seen",
    connections:    "Connections",
  };

  // Build headers array based on actual data
  const finalKeys    = keyOrder.filter((k) => has[k]);
  const finalHeaders = finalKeys.map((k) => labels[k]);

  // Build csvData rows
  const csvData = rows.map((row) =>
    finalKeys.map((key) => row[key] ?? "")
  );

  // CSV field escaping
  const escapeCsvField = (field) => {
    if (field === null || field === undefined) return "";
    const str = String(field);
    if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("'") || str.includes(":")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Join into CSV content
  const csvContent = [
    finalHeaders.join(","),
    ...csvData.map((row) => row.map(escapeCsvField).join(","))
  ].join("\n");

  // Trigger download via Blob + link
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const fetchAllPages = async (filtersToUse) => {
    let allCustomers = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        // Use the hook function but await its result
        const response = await attemptGetCustomers({
          ...filtersToUse,
          page: currentPage,
        });

        if (!response?.data) {
          console.error("Invalid response format:", response);
          break;
        }

        const { data, has_more, total_count } = response.data;

        // Verify and fix any inconsistent data before adding to allCustomers
        const processedData = data.map((customer) => {
          // Make sure locations and locations_data are consistent
          if (customer.locations_data && Array.isArray(customer.locations_data)) {
            // Ensure locations array matches locations_data
            customer.locations = customer.locations_data.map((loc) => loc.location);

            // Make sure connections count is accurate
            if (customer.locations_data.length !== customer.connections) {
              console.warn(`Fixed connections count for ${customer.email_address} from ${customer.connections} to ${customer.locations_data.length}`);
              customer.connections = customer.locations_data.length;
            }
          }
          return customer;
        });

        allCustomers = [...allCustomers, ...processedData];
        hasMore = has_more;

        // Calculate and update progress
        const progress = Math.round((allCustomers.length / total_count) * 100);
        setExportProgress(progress);

        currentPage++;
      } catch (error) {
        console.error("Error fetching page", currentPage, error);
        hasMore = false;
      }
    }

    return allCustomers;
  };

  const handleExportAll = async () => {
    try {
      setIsExporting(true);
      setExportProgress(5);

      const allData = await fetchAllPages(allFilters);
      downloadCSV(allData, "all_contacts.csv");
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleExportFiltered = async () => {
    try {
      setIsExporting(true);
      setExportProgress(5);

      const filteredData = await fetchAllPages(filters);
      downloadCSV(filteredData, "filtered_contacts.csv");
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleHeaderClick = (column) => {
    setSortColumn((oldColumn) => {
      if (oldColumn !== column) return column;
      return column;
    });

    setSortDirection((oldDirection) => {
      if (!oldDirection) return "desc";
      if (oldDirection === "desc") return "asc";
      return null;
    });

    queryClient.invalidateQueries(["filteredCustomers"]);
  };

  const customers = useMemo(() => {
    if (!customerData?.pages) return [];
    // Flatten all pages into a single array
    return customerData.pages.flatMap((page) => page.data);
  }, [customerData?.pages]);

  const bottomRef = useRef(null);

  // Add this effect for intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) {
        observer.unobserve(bottomRef.current);
      }
    };
  }, [bottomRef, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const [showAgeColumn, setShowAgeColumn] = useState(true);
  const [showDobColumn, setShowDobColumn] = useState(true);
  const [showGenderColumn, setShowGenderColumn] = useState(true);
  const [showSmsMarketingColumn, setShowSmsMarketingColumn] = useState(true);

  useEffect(() => {
    if (customers && customers.length > 0) {
      // Check if any customer has age data
      const hasAgeData = customers.some((customer) => customer.age && customer.age !== "field_disabled" && customer.age !== "");
      setShowAgeColumn(hasAgeData);

      // Check if any customer has DOB data
      const hasDobData = customers.some((customer) => customer.dob !== null && customer.dob !== "");
      setShowDobColumn(hasDobData);

      // Check if any customer has gender data
      const hasGenderData = customers.some((customer) => customer.gender && customer.gender !== "Field_disabled" && customer.gender !== "");
      setShowGenderColumn(hasGenderData);

      // Check if any customer has phone number data
      const hasPhoneNumber = customers.some((customer) => customer.phone_number !== null && customer.phone_number !== "");
      setShowSmsMarketingColumn(hasPhoneNumber);
    }
  }, [customers]);

  // Helper function to check if customer has multiple marketing statuses
  const hasMultipleLocations = (customer) => {
    // Show "Multiple" badge if there are 2+ locations, regardless of status
    return customer.locations_data && customer.locations_data.length > 1;
  };

  const areAllStatusesSubscribed = (customer) => {
    if (!customer.locations_data || customer.locations_data.length === 0) {
      return false;
    }

    return customer.locations_data.every((loc) => loc.email_marketing_status === "Subscribed");
  };

  const areAllSmsStatusesSubscribed = (customer) => {
    if (!customer.locations_data || customer.locations_data.length === 0) {
      return false;
    }
    return customer.locations_data?.every(
      (loc) => loc.sms_marketing_status === "Subscribed"
    );
  }

  const isPageLoading = isIntegrationsLoading || isAirshipStatusLoading || isZonesLoading || isBrandsLoading;
  if (isPageLoading) {
    return (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  return (
    <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", height: "100%" }}>
      { isLoadingCustomerData && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      {isExporting && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
          <Progress value={exportProgress} max={100} size="1" radius="none" />
        </div>
      )}
      {/* Customers Page Header Section */}
      <Flex direction="column" gap="3" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
        <Flex justify="between" gap="3" pt="1" style={{ flex: "1 0 0", alignItems: "center", alignSelf: "stretch" }}>
          <TextField.Root
            placeholder="Search email..."
            value={emailSearchInput}
            onChange={(event) => setEmailSearchInput(event.target.value)}
            style={{ width: "240px", flexShrink: 0 }}
            aria-label="Search contacts by email"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon />
            </TextField.Slot>
          </TextField.Root>
          <Flex justify="center" align="center" gap="4">
            {/* Export and count in their own group with proper spacing */}
            <Flex align="center" gap="5">
              {/* Export dropdown */}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Button size="2" variant="soft" color="gray">
                    Export
                    <ChevronDownIcon width="16" height="16" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item onClick={handleExportAll}>
                    Export all
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={handleExportFiltered}>
                    Export current filter
                  </DropdownMenu.Item>
                  {!isMailchimpActive() && (
                    <>
                      <DropdownMenu.Separator />
                      <DropdownMenu.Item onClick={() => navigate("/integrations/browse-all")}>
                        Integrate Mailchimp
                      </DropdownMenu.Item>
                    </>
                  )}
                  {!isAirshipActive() && (
                    <>
                      {isMailchimpActive() ? <DropdownMenu.Separator /> : null}
                      <DropdownMenu.Item onClick={() => navigate("/integrations/browse-all")}>
                        Integrate Airship
                      </DropdownMenu.Item>
                    </>
                  )}
                  {!isMetaActive() && (
                    <>
                      {isMailchimpActive() ? <DropdownMenu.Separator /> : null}
                      <DropdownMenu.Item onClick={() => navigate("/integrations/browse-all")}>
                        Integrate Meta Ads
                      </DropdownMenu.Item>
                    </>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Flex>
            {/* Filter options in their own group */}
            <Flex align="center" gap="2">
              {/* First: FilterByMultiSelect */}
              <FilterByMultiSelect options={filterOptions} defaultValue={selectedCategories} onValueChange={handleCategoryChange} />
              {zoneOptions.length > 0 && <ZonesMultiSelect options={zoneOptions} defaultValue={zoneSelection} onValueChange={setZoneSelection} />}

              <LocationMultiSelect
                options={showBrandFilter ? filteredLocationOptions : initialItems}
                defaultValue={locationSelection}
                onValueChange={(value) => {
                  setLocationSelection(value);
                }}
              />

              {showBrandFilter && <BrandMultiSelect options={formattedBrandOptions} defaultValue={brandSelection} onValueChange={setBrandSelection} />}
            </Flex>
          </Flex>
        </Flex>
        {/* Separate container for conditional filters - only renders if at least one category is selected */}
        {selectedCategories.length > 0 && (
          <Flex className="filter-bar-bottom" justify="end" align="center" gap="2" style={{ alignSelf: "stretch"}}>
            {/* <Text weight="medium" size="3" >Filters</Text> */}
            {selectedCategories.includes("email_marketing_status") && (
              <MultiSelect
                options={marketingOptions}
                defaultValue={marketingOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setMarketingSelection(value);
                }}
                placeholder="Email marketing"
              />
            )}

            {selectedCategories.includes("sms_marketing_status") && (
              <MultiSelect
                options={smsMarketingOptions}
                defaultValue={smsMarketingOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setSmsMarketingSelection(value);
                }}
                placeholder="SMS marketing"
              />
            )}

            {selectedCategories.includes("age") && (
              <MultiSelect
                options={ageOptions}
                defaultValue={ageOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setAgeSelection(value);
                }}
                placeholder="Age"
              />
            )}

            {selectedCategories.includes("gender") && (
              <MultiSelect
                options={genderOptions}
                defaultValue={genderOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setGenderSelection(value);
                }}
                placeholder="Gender"
              />
            )}
            {selectedCategories.includes("auth_type") && (
              <MultiSelect
                options={authTypeOptions}
                defaultValue={authTypeOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setAuthTypeSelection(value);
                }}
                placeholder="Access method"
              />
            )}
            {selectedCategories.includes("created_on") && (
              <Flex align="center" gap="2">
                <Text size="2" weight="medium" style={{ whiteSpace: "nowrap" }}>Created on</Text>
                <DatePickerWithRange
                  value={createdOnDateRange}
                  onDateChange={setCreatedOnDateRange}
                  allowFutureDates={false}
                  numberOfMonths={2}
                />
              </Flex>
            )}
            {selectedCategories.includes("last_seen") && (
              <Flex align="center" gap="2">
                <Text size="2" weight="medium" style={{ whiteSpace: "nowrap" }}>Last seen</Text>
                <DatePickerWithRange
                  value={lastSeenDateRange}
                  onDateChange={setLastSeenDateRange}
                  allowFutureDates={false}
                  numberOfMonths={2}
                />
              </Flex>
            )}
            <Button size="2" variant="soft" color="red" onClick={handleClearFilters}>
              <Cross2Icon />
              Clear filters
            </Button>
          </Flex>
        )}
      </Flex>

      {/* Customers Page Data Section */}
      <Flex className="contacts-section" direction="column" gap="5" style={{ alignSelf: "stretch", 
        alignItems: "flex-start", flex: "1 0 0", height: "100%" }}>
        {/* Customers Table */}
        {!isLoadingCustomerData && customers?.length === 0 ? (
          <NoResults />
        ) : (
          <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", 
            height: "100%" }}>
            {/* For the total contacts, total emails, and opt in value cards */}
            <Flex gap="5" style={{ alignSelf: "stretch"}}>
              <Card style={{ display: "flex", padding: 'var(--space-4)', flexDirection: 'column', justifyContent: 'space-between',
                alignItems: 'flex-start', flex: '1 0 0', alignSelf: 'stretch', height: '100%' }}>
                <Flex direction="column" gap="5">
                    <TruncatedText color="gray" size="2" weight="medium">
                        Customer contacts
                    </TruncatedText>
                    <TruncatedText color="gray-12" size="6" weight="bold">
                        { trueTotalContacts }
                    </TruncatedText>
                  </Flex>
              </Card>
              
              <Card style={{ display: "flex", padding: 'var(--space-4)', flexDirection: 'column', justifyContent: 'space-between',
                alignItems: 'flex-start', flex: '1 0 0', alignSelf: 'stretch', height: '100%' }}>
                <Flex direction="column" gap="5">
                    <TruncatedText color="gray" size="2" weight="medium">
                        Emails or SMS subscribed
                    </TruncatedText>
                    <TruncatedText color="gray-12" size="6" weight="bold">
                        { activeSubscriptions }
                    </TruncatedText>
                </Flex>
              </Card>
              
              <Card style={{ display: "flex", padding: 'var(--space-4)', flexDirection: 'column', justifyContent: 'space-between',
                alignItems: 'flex-start', flex: '1 0 0', alignSelf: 'stretch', height: '100%' }}>
                <Flex direction="column" gap="5">
                    <TruncatedText color="gray" size="2" weight="medium">
                        Marketing opt-in rate
                    </TruncatedText>
                    <TruncatedText color="gray-12" size="6" weight="bold">
                        { optInRate + "%"}
                    </TruncatedText>
                </Flex>
              </Card>
            </Flex>
            <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start", 
              flex: "1 0 0", height: "100%", maxWidth: "100%", overflow: 'auto'}}>
              <Table.Root layout="fixed" style={{ width: "100%", height: "100%"}}>
                {/* Table Header */}
                <Table.Header style={{ position: "sticky", top: "0", zIndex: "1", backgroundColor: "var(--color-panel-solid)" }}>
                  <Table.Row align="center">
                    {/* Email address column - always visible */}
                    <Table.ColumnHeaderCell style={{ cursor: "default" }}>
                      <Flex align="center" gap="1">
                        <TruncatedText size="2" weight="medium">
                          Contact
                        </TruncatedText>
                        {sortColumn === "email_address" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                      </Flex>
                    </Table.ColumnHeaderCell>
                    {/* Email marketing column - always visible */}
                    <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("email_marketing_status")}>
                      <Flex align="center" gap="1" style={{ width: "100%" }}>
                        <TruncatedText size="2" weight="medium">
                          Email marketing
                        </TruncatedText>
                        {sortColumn === "email_marketing_status" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                      </Flex>
                    </Table.ColumnHeaderCell>

                    {/* SMS marketing column - conditional as well */}
                    {showSmsMarketingColumn && (
                      <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("sms_marketing_status")}>
                        <Flex align="center" gap="1" style={{ width: "100%" }}>
                          <TruncatedText size="2" weight="medium">
                            SMS marketing
                          </TruncatedText>
                          {sortColumn === "sms_marketing_status" && (
                            sortDirection === "asc" ? <CaretUpIcon /> :
                            sortDirection === "desc" ? <CaretDownIcon /> :
                            <CaretSortIcon />
                          )}
                        </Flex>
                      </Table.ColumnHeaderCell>
                    )}
                    
                    {/* Conditional columns - Age */}
                    {showAgeColumn && (
                      <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("age")}>
                        <Flex align="center" gap="1">
                          <TruncatedText size="2" weight="medium">
                            Age group
                          </TruncatedText>
                          {sortColumn === "age" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                        </Flex>
                      </Table.ColumnHeaderCell>
                    )}
                    {/* Conditional columns - Date of Birth */}
                    {showDobColumn && (
                      <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("dob")}>
                        <Flex align="center" gap="1">
                          <TruncatedText size="2" weight="medium">
                            Date of birth
                          </TruncatedText>
                          {sortColumn === "dob" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                        </Flex>
                      </Table.ColumnHeaderCell>
                    )}
                    {/* Conditional columns - Gender */}
                    {showGenderColumn && (
                      <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("gender")}>
                        <Flex align="center" gap="1">
                          <TruncatedText size="2" weight="medium">
                            Gender
                          </TruncatedText>
                          {sortColumn === "gender" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                        </Flex>
                      </Table.ColumnHeaderCell>
                    )}
                    {/* Conditional column - Brand(s) */}
                    {showBrandFilter && (
                      <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("brands")}>
                        <Flex align="center" gap="1">
                          <TruncatedText size="2" weight="medium">
                            Brand(s)
                          </TruncatedText>
                          {sortColumn === "brands" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                        </Flex>
                      </Table.ColumnHeaderCell>
                    )}
                    {/* Location(s) column - always visible */}
                    <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("locations")}>
                      <Flex align="center" gap="1">
                        <TruncatedText size="2" weight="medium">
                          Location(s)
                        </TruncatedText>
                        {sortColumn === "locations" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                      </Flex>
                    </Table.ColumnHeaderCell>
                    
                    {/* Created column - always visible */}
                    <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("created")}>
                      <Flex align="center" gap="1">
                        <TruncatedText size="2" weight="medium">
                          Created on
                        </TruncatedText>
                        {sortColumn === "created" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                      </Flex>
                    </Table.ColumnHeaderCell>
                    {/* Last seen column - always visible */}
                    <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("last_seen")}>
                      <Flex align="center" gap="1">
                        <TruncatedText size="2" weight="medium">
                          Last seen
                        </TruncatedText>
                        {sortColumn === "last_seen" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                      </Flex>
                    </Table.ColumnHeaderCell>
                    {/* Connections column - always visible */}
                    <Table.ColumnHeaderCell style={{ cursor: "default" }} onClick={() => handleHeaderClick("connections")}>
                      <Flex align="center" gap="1">
                        <TruncatedText size="2" weight="medium">
                          Connection(s)
                        </TruncatedText>
                        {sortColumn === "connections" && (sortDirection === "asc" ? <CaretUpIcon /> : sortDirection === "desc" ? <CaretDownIcon /> : <CaretSortIcon />)}
                      </Flex>
                    </Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>

                {/* Table Body */}
                <Table.Body>
                  {!isLoadingCustomerData &&
                    customers?.map((customer, index) => (
                      <Table.Row key={index} align="center">
                        <Table.Cell>
                          <Flex gap="1" direction="column">
                            {(customer.first_name || customer.last_name) && (
                              <TruncatedText weight="medium" size="2" color="gray-12">
                                {((customer.first_name || "") + (customer.last_name ? (" " + customer.last_name) : "")).trim()}
                              </TruncatedText>)
                            }
                            <Flex gap="1" style={{ alignItems: 'center', alignSelf: 'stretch'}}>
                              { customer.auth_type === "facebook" &&
                                <Tooltip content="Authorised with Facebook">
                                  <FacebookIcon width="12" height="12"/>
                                </Tooltip>
                              }
                              {customer.auth_type === "google" &&
                                <Tooltip content="Authorised with Google">
                                  <GoogleIcon width="12" height="12"/>
                                </Tooltip>
                              }
                              {customer.auth_type === "apple" &&
                                <Tooltip content="Authorised with Apple">
                                  <AppleIcon width="12" height="12"/>
                                </Tooltip>
                              }
                              {!customer.email_address.endsWith("@noemailbwifi.com") && (
                                <TruncatedText weight="regular" size="2" color="gray">
                                  {customer.email_address}
                                </TruncatedText>
                              )}
                            </Flex>
                            {customer.phone_number && (
                                <TruncatedText weight="regular" size="2" color="gray">
                                  {formatInternationalPhoneNumber(customer.phone_number)}
                                </TruncatedText>
                            )}
                          </Flex>
                        </Table.Cell>
                        <Table.Cell>
                          {hasMultipleLocations(customer) ? (
                            <HoverCard.Root>
                              <HoverCard.Trigger>
                                <Flex style={{ width: "100%" }}>
                                  <Badge size="1" variant="soft" color={areAllStatusesSubscribed(customer) ? "blue" : "gray"} 
                                    style={{ cursor: "default", maxWidth: "100%" }}>
                                    <TruncatedText>Multiple</TruncatedText>
                                  </Badge>
                                </Flex>
                              </HoverCard.Trigger>
                              <HoverCard.Content maxHeight="300px" style={{ padding: "var(--space-3)" }}>
                                <Flex direction="column" gap="2">
                                  {customer.locations_data.map((loc, idx) => (
                                    <Flex key={idx} justify="between" align="center" gap="3">
                                      <Text size="2">{loc.location}</Text>
                                      <Badge size="1" variant="soft" color={loc.email_marketing_status === "Subscribed" ? "blue" : "gray"}>
                                        <TruncatedText>{loc.email_marketing_status || "Unknown"}</TruncatedText>
                                      </Badge>
                                    </Flex>
                                  ))}
                                </Flex>
                              </HoverCard.Content>
                            </HoverCard.Root>
                          ) : (
                            <Flex style={{ width: "100%" }}>
                              <Badge size="1" variant="soft" color={customer.email_marketing_status === "Subscribed" ? "blue" : "gray"}
                                style={{ maxWidth: "100%" }}>
                                <TruncatedText>{customer.email_marketing_status}</TruncatedText>
                              </Badge>
                            </Flex>
                          )}
                        </Table.Cell>

                        {showSmsMarketingColumn && (
                          <Table.Cell>
                            {hasMultipleLocations(customer) ? (
                              <HoverCard.Root>
                                <HoverCard.Trigger>
                                  <Flex style={{ width: "100%" }}>
                                    <Badge size="1" variant="soft" color={areAllSmsStatusesSubscribed(customer) ? "blue" : "gray"} 
                                      style={{ cursor: "default", maxWidth: "100%" }}>
                                      <TruncatedText>Multiple</TruncatedText>
                                    </Badge>
                                  </Flex>
                                </HoverCard.Trigger>
                                <HoverCard.Content maxHeight="300px" style={{ padding: "var(--space-3)" }}>
                                  <Flex direction="column" gap="2">
                                    {customer.locations_data.map((loc, idx) => (
                                      <Flex key={idx} justify="between" align="center" gap="3">
                                        <Text size="2">{loc.location}</Text>
                                        <Badge
                                          size="1"
                                          variant="soft"
                                          color={loc.sms_marketing_status === "Subscribed" ? "blue" : "gray"}
                                        >
                                          <TruncatedText>{loc.sms_marketing_status || "Unknown"}</TruncatedText>
                                        </Badge>
                                      </Flex>
                                    ))}
                                  </Flex>
                                </HoverCard.Content>
                              </HoverCard.Root>
                            ) : (
                              <Flex style={{ width: "100%" }}>
                                <Badge
                                  size="1"
                                  variant="soft"
                                  color={customer.sms_marketing_status === "Subscribed" ? "blue" : "gray"}
                                  style={{ maxWidth: "100%" }}
                                >
                                  <TruncatedText>{customer.sms_marketing_status}</TruncatedText>
                                </Badge>
                              </Flex>
                            )}
                          </Table.Cell>
                        )}
                        
                        {showAgeColumn && (
                          <Table.Cell>
                            <TruncatedText weight="regular" size="2" color="gray">
                              {customer.age === "field_disabled" ? "" : customer.age}
                            </TruncatedText>
                          </Table.Cell>
                        )}
                        {showDobColumn && (
                          <Table.Cell>
                            <TruncatedText weight="regular" size="2" color="gray">
                              {customer.dob === null ? "" : customer.dob}
                            </TruncatedText>
                          </Table.Cell>
                        )}
                        {showGenderColumn && (
                          <Table.Cell>
                            <TruncatedText weight="regular" size="2" color="gray">
                              {customer.gender === "Field_disabled" 
                                ? "" 
                                : (customer.gender === "Others" ? "Other" : customer.gender)
                              }
                            </TruncatedText>
                          </Table.Cell>
                        )}
                        {showBrandFilter && (
                          <Table.Cell>
                            <TruncatedText weight="regular" size="2" color="gray">
                              {customer.brands ? customer.brands.join(", ") : ""}
                            </TruncatedText>
                          </Table.Cell>
                        )}
                        <Table.Cell>
                          <TruncatedText weight="regular" size="2" color="gray">
                            {customer.locations.join(", ")}
                          </TruncatedText>
                        </Table.Cell>
                        
                        <Table.Cell>
                          <TruncatedText weight="regular" size="2" color="gray">
                            {customer.created ? new Date(customer.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' + new Date(customer.created).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }) : ""}
                          </TruncatedText>
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedText weight="regular" size="2" color="gray">
                            {customer.last_seen ? new Date(customer.last_seen).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' + new Date(customer.last_seen).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }) : ""}
                          </TruncatedText>
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedText weight="regular" size="2" color="gray">
                            {customer.connections || 0}
                          </TruncatedText>
                        </Table.Cell>
                      </Table.Row>
                    ))}

                  {/* Infinite scroll trigger element */}
                  <div ref={bottomRef} style={{ height: "1px" }} />
                </Table.Body>
              </Table.Root>
            </Card>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
